import { FC, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import {
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  Edit,
  Cancel,
  AccessTime,
  Business,
  Star,
  Archive,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { CONSTANTS } from "../../../utils/CONSTS";
import { pages } from "../../../utils/common";
import { logger } from "../../../utils/common";
import {
  usePremiseByIdHook,
  useUpdatePremiseHook,
  useArchivePremiseHook,
} from "../../../hooks/useSpocHook";
import {
  TPremise,
  TOperatingHours,
} from "../../../apis/spocPremise/spocPremise.interfaces";

const daysOfWeek = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
] as const;

const commonFacilities = [
  "Library",
  "Laboratory",
  "Computer Lab",
  "Sports Facilities",
  "Cafeteria",
  "Auditorium",
  "Parking",
  "Security",
  "WiFi",
  "Air Conditioning",
  "Playground",
  "Medical Room",
  "Transportation",
];

// Helper function to convert time string (HH:MM) to minutes for comparison
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const dayOperatingHoursSchema = z
  .object({
    open: z.string().optional(),
    close: z.string().optional(),
    closed: z.boolean().optional(),
  })
  .refine(
    (data) => {
      // If day is closed, no validation needed
      if (data.closed) return true;
      // If both open and close are provided, validate close > open
      if (data.open && data.close) {
        return timeToMinutes(data.close) > timeToMinutes(data.open);
      }
      // If only one is provided, it's invalid
      if (data.open && !data.close) return false;
      if (data.close && !data.open) return false;
      // If neither is provided, it's valid (not configured)
      return true;
    },
    {
      message: "Close time must be after open time",
    },
  );

const operatingHoursSchema = z.object({
  monday: dayOperatingHoursSchema.optional(),
  tuesday: dayOperatingHoursSchema.optional(),
  wednesday: dayOperatingHoursSchema.optional(),
  thursday: dayOperatingHoursSchema.optional(),
  friday: dayOperatingHoursSchema.optional(),
  saturday: dayOperatingHoursSchema.optional(),
  sunday: dayOperatingHoursSchema.optional(),
});

const premiseConfigurationSchema = z.object({
  operatingHours: operatingHoursSchema.optional(),
  facilities: z.array(z.string()).optional(),
  specialFeatures: z
    .string()
    .max(1000, "Special features must not exceed 1000 characters")
    .optional(),
});

type PremiseConfigurationFormData = z.infer<typeof premiseConfigurationSchema>;

const PremiseDetailView: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  const { data: premise, isLoading, error } = usePremiseByIdHook(id);
  const updatePremiseMutation = useUpdatePremiseHook();
  const archivePremiseMutation = useArchivePremiseHook();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PremiseConfigurationFormData>({
    resolver: zodResolver(premiseConfigurationSchema),
    defaultValues: {
      operatingHours: {},
      facilities: [],
      specialFeatures: "",
    },
  });

  const watchedOperatingHours = watch("operatingHours");

  useEffect(() => {
    if (premise) {
      reset({
        operatingHours: premise.operatingHours || {},
        facilities: premise.facilities || [],
        specialFeatures: premise.specialFeatures || "",
      });
    }
  }, [premise, reset]);

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    if (premise) {
      reset({
        operatingHours: premise.operatingHours || {},
        facilities: premise.facilities || [],
        specialFeatures: premise.specialFeatures || "",
      });
    }
  };

  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!id) return;

    try {
      await archivePremiseMutation.mutateAsync(id);
      logger.show_success("Premise archived successfully!");
      setArchiveDialogOpen(false);
      navigate(`${pages.spocPremise.path}/premises`);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to archive premise. Please try again.",
      );
    }
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
  };

  const onSubmit = async (data: PremiseConfigurationFormData) => {
    if (!premise) return;

    try {
      // Transform operatingHours to match TOperatingHours type
      const transformedOperatingHours: TOperatingHours | undefined =
        data.operatingHours
          ? Object.entries(data.operatingHours).reduce(
              (acc, [day, dayData]) => {
                if (
                  dayData &&
                  !dayData.closed &&
                  dayData.open &&
                  dayData.close
                ) {
                  acc[day as keyof TOperatingHours] = {
                    open: dayData.open,
                    close: dayData.close,
                    closed: false,
                  };
                } else if (dayData?.closed) {
                  acc[day as keyof TOperatingHours] = {
                    open: "00:00",
                    close: "00:00",
                    closed: true,
                  };
                }
                return acc;
              },
              {} as TOperatingHours,
            )
          : undefined;

      await updatePremiseMutation.mutateAsync({
        id: premise.id,
        request: {
          operatingHours: transformedOperatingHours,
          facilities: data.facilities,
          specialFeatures: data.specialFeatures,
        },
      });
      logger.show_success("Premise configuration updated successfully!");
      setIsEditMode(false);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to update premise configuration. Please try again.",
      );
    }
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.spocPremise} - Premise Detail`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="premise-detail-view"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !premise) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.spocPremise} - Premise Detail`}
        />
        <Box p={3} data-testid="premise-detail-view">
          <MainTitle title="Premise Detail" />
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            data-testid="premise-detail-error"
          >
            {error?.message || "Premise not found. Please try again later."}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`${pages.spocPremise.path}/premises`)}
            sx={{ mt: 2 }}
            data-testid="back-to-premise-list-error-button"
          >
            Back to List
          </Button>
        </Box>
      </>
    );
  }

  const isSaving = isSubmitting || updatePremiseMutation.isPending;
  const isError = updatePremiseMutation.isError;
  const errorMessage = updatePremiseMutation.error?.message;

  return (
    <>
      <DocumentTitle
        title={`${CONSTANTS.title.spocPremise} - ${premise.name}`}
      />

      <Grid container spacing={3} data-testid="premise-detail-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="premise-detail-header"
          >
            <MainTitle title="Premise Configuration" />
            <Box display="flex" gap={2} data-testid="premise-detail-actions">
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate(`${pages.spocPremise.path}/premises`)}
                data-testid="back-to-premise-list-button"
              >
                Back to List
              </Button>
              {!isEditMode && premise.status !== "archived" && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={handleEdit}
                    data-testid="edit-premise-config-button"
                  >
                    Edit Configuration
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Archive />}
                    onClick={handleArchiveClick}
                    data-testid="archive-premise-button"
                  >
                    Archive
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="premise-info-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {premise.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {premise.address}
              </Typography>
              <Box display="flex" gap={1} sx={{ mt: 1 }}>
                <Chip
                  label={
                    premise.status.charAt(0).toUpperCase() +
                    premise.status.slice(1)
                  }
                  color={
                    premise.status === "active"
                      ? "success"
                      : premise.status === "inactive"
                        ? "default"
                        : "error"
                  }
                  size="small"
                />
                <Chip
                  label={`Capacity: ${premise.capacity}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {isEditMode ? (
          <Grid item xs={12}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              data-testid="premise-config-form"
            >
              <Card data-testid="premise-config-form-card">
                <CardContent>
                  {isError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {errorMessage ||
                        "Failed to update premise configuration. Please try again."}
                    </Alert>
                  )}

                  {/* Operating Hours Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 2 }}
                    >
                      <AccessTime color="primary" />
                      <Typography variant="h6">Operating Hours</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      {daysOfWeek.map((day) => {
                        const dayKey = day.key as keyof TOperatingHours;
                        const dayData = watchedOperatingHours?.[dayKey];
                        const isClosed = dayData?.closed || false;

                        return (
                          <Grid item xs={12} sm={6} md={4} key={day.key}>
                            <Card variant="outlined">
                              <CardContent>
                                <FormControlLabel
                                  control={
                                    <Controller
                                      name={`operatingHours.${day.key}.closed`}
                                      control={control}
                                      render={({ field }) => (
                                        <Checkbox
                                          {...field}
                                          checked={field.value || false}
                                          onChange={(e) => {
                                            field.onChange(e.target.checked);
                                            if (e.target.checked) {
                                              // Clear times when closed
                                              setValue(
                                                `operatingHours.${day.key}.open`,
                                                "",
                                              );
                                              setValue(
                                                `operatingHours.${day.key}.close`,
                                                "",
                                              );
                                            }
                                          }}
                                        />
                                      )}
                                    />
                                  }
                                  label={day.label}
                                />
                                {!isClosed && (
                                  <Box sx={{ mt: 1 }}>
                                    <Box display="flex" gap={1}>
                                      <ControlledTextField
                                        control={control as any}
                                        name={`operatingHours.${day.key}.open`}
                                        id={`operating-hours-${day.key}-open`}
                                        label="Open"
                                        type="time"
                                        isGridItem={false}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                      />
                                      <ControlledTextField
                                        control={control as any}
                                        name={`operatingHours.${day.key}.close`}
                                        id={`operating-hours-${day.key}-close`}
                                        label="Close"
                                        type="time"
                                        isGridItem={false}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        errorMessage={(() => {
                                          const dayError =
                                            errors.operatingHours?.[
                                              day.key as keyof typeof errors.operatingHours
                                            ];
                                          if (!dayError) return undefined;
                                          if (typeof dayError === "string")
                                            return dayError;
                                          return (dayError as { message?: string })
                                            ?.message;
                                        })()}
                                      />
                                    </Box>
                                  </Box>
                                )}
                                {isClosed && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 4 }}
                                  >
                                    Closed
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>

                  {/* Facilities Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 2 }}
                    >
                      <Business color="primary" />
                      <Typography variant="h6">Facilities Available</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Controller
                      name="facilities"
                      control={control}
                      render={({ field }) => (
                        <FormGroup>
                          <Grid container spacing={1}>
                            {commonFacilities.map((facility) => (
                              <Grid item xs={12} sm={6} md={4} key={facility}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        field.value?.includes(facility) || false
                                      }
                                      onChange={(e) => {
                                        const currentFacilities =
                                          field.value || [];
                                        if (e.target.checked) {
                                          field.onChange([
                                            ...currentFacilities,
                                            facility,
                                          ]);
                                        } else {
                                          field.onChange(
                                            currentFacilities.filter(
                                              (f) => f !== facility,
                                            ),
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label={facility}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </FormGroup>
                      )}
                    />
                  </Box>

                  {/* Special Features Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 2 }}
                    >
                      <Star color="primary" />
                      <Typography variant="h6">Special Features</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <ControlledTextField
                      control={control as any}
                      name="specialFeatures"
                      id="special-features"
                      label="Special Features"
                      multiline
                      rows={4}
                      errorMessage={errors.specialFeatures?.message}
                      placeholder="Describe any special features or unique characteristics of this premise..."
                    />
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    gap={2}
                    sx={{ mt: 3 }}
                    data-testid="premise-config-form-actions"
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      disabled={isSaving}
                      data-testid="cancel-premise-config-button"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        isSaving ? <CircularProgress size={16} /> : <Save />
                      }
                      disabled={isSaving}
                      data-testid="save-premise-config-button"
                    >
                      {isSaving ? "Saving..." : "Save Configuration"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </form>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Card data-testid="premise-config-display-card">
              <CardContent>
                {/* Operating Hours Display */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 2 }}
                  >
                    <AccessTime color="primary" />
                    <Typography variant="h6">Operating Hours</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {premise.operatingHours &&
                  Object.keys(premise.operatingHours).length > 0 ? (
                    <Grid container spacing={2}>
                      {daysOfWeek.map((day) => {
                        const dayKey = day.key as keyof TOperatingHours;
                        const dayData = premise.operatingHours?.[dayKey];
                        const isClosed = dayData?.closed;

                        return (
                          <Grid item xs={12} sm={6} md={4} key={day.key}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                >
                                  {day.label}
                                </Typography>
                                {isClosed ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Closed
                                  </Typography>
                                ) : dayData?.open && dayData?.close ? (
                                  <Typography variant="body2">
                                    {dayData.open} - {dayData.close}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Not configured
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Operating hours not configured
                    </Typography>
                  )}
                </Box>

                {/* Facilities Display */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 2 }}
                  >
                    <Business color="primary" />
                    <Typography variant="h6">Facilities Available</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {premise.facilities && premise.facilities.length > 0 ? (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {premise.facilities.map((facility) => (
                        <Chip key={facility} label={facility} size="small" />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No facilities configured
                    </Typography>
                  )}
                </Box>

                {/* Special Features Display */}
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ mb: 2 }}
                  >
                    <Star color="primary" />
                    <Typography variant="h6">Special Features</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {premise.specialFeatures ? (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {premise.specialFeatures}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No special features configured
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
        maxWidth="sm"
        fullWidth
        data-testid="archive-premise-dialog"
      >
        <DialogTitle data-testid="archive-premise-dialog-title">
          Archive Premise
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive this premise?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Archiving will mark this premise as archived. Archived premises
            cannot be edited and will be filtered out from the default premise
            list view. You can still view archived premises by using the filter
            option.
          </DialogContentText>
          {archivePremiseMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {archivePremiseMutation.error?.message ||
                "Failed to archive premise. Please try again."}
            </Alert>
          )}
        </DialogContent>
        <DialogActions data-testid="archive-premise-dialog-actions">
          <Button
            onClick={handleArchiveCancel}
            disabled={archivePremiseMutation.isPending}
            data-testid="cancel-archive-premise-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleArchiveConfirm}
            color="error"
            variant="contained"
            disabled={archivePremiseMutation.isPending}
            startIcon={
              archivePremiseMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <Archive />
              )
            }
            data-testid="confirm-archive-premise-button"
          >
            {archivePremiseMutation.isPending
              ? "Archiving..."
              : "Archive Premise"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PremiseDetailView;
